/**
 * MIT License
 *
 * Copyright (c) 2021 inkOfPixel srl
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * https://github.com/inkOfPixel/sms-length
 *
 */

export type Encoding = "GSM_7BIT" | "GSM_7BIT_EXT" | "UTF16";

const GSM_7BIT_REGEXP =
  /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà]*$/;
const GSM_7BIT_EXT_REGEXP =
  /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~\]|€]*$/;
const GSM_7BIT_EXT_CHAR_REGEXP = /^[\^{}\\[~\]|€]$/;

const messageLength: { [key in Encoding]: number } = {
  GSM_7BIT: 160,
  GSM_7BIT_EXT: 160,
  UTF16: 70,
};

const multiMessageLength: { [key in Encoding]: number } = {
  GSM_7BIT: 153,
  GSM_7BIT_EXT: 153,
  UTF16: 67,
};

export const count = (text: string) => {
  const encoding = detectEncoding(text);
  let length = text.length;

  if (encoding === "GSM_7BIT_EXT") {
    length += countGsm7bitExt(text);
  }

  let characterPerMessage = messageLength[encoding];
  if (length > characterPerMessage) {
    characterPerMessage = multiMessageLength[encoding];
  }

  const messages = Math.ceil(length / characterPerMessage);

  let inCurrentMessage = length;
  if (messages > 0) {
    inCurrentMessage = length - characterPerMessage * (messages - 1);
  }

  let remaining = characterPerMessage * messages - length;
  if (remaining === 0 && messages === 0) {
    remaining = characterPerMessage;
  }

  return {
    encoding,
    length,
    characterPerMessage,
    inCurrentMessage,
    remaining,
    messages,
  };
};

const detectEncoding = (text: string): Encoding => {
  switch (false) {
    case text.match(GSM_7BIT_REGEXP) == null:
      return "GSM_7BIT";
    case text.match(GSM_7BIT_EXT_REGEXP) == null:
      return "GSM_7BIT_EXT";
    default:
      return "UTF16";
  }
};

const countGsm7bitExt = (text: string) => {
  const extChar = [];
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if ((char.match(GSM_7BIT_EXT_CHAR_REGEXP) || []).length > 0) {
      extChar.push(char);
    }
  }
  return extChar.length;
};
